import {
  palettes,
  PaletteOptions,
} from '@dewise-devex/backstage-plugin-mui-uikit-react';

/**
 * Defines the color palette options for the dark theme in the Backstage application.
 *
 * This configuration extends the default `palettes.dark` from `@dewise-devex/backstage-plugin-mui-uikit-react`
 * and customizes colors for various UI elements such as `primary`, `secondary`,
 * `background`, and `navigation`.
 *
 * @category Theming
 */
export const darkPalette: PaletteOptions = {
  ...palettes.dark,

  /** Primary color used for key UI components and actions. */
  primary: {
    main: '#CFBCFF',
    container: '#5800D4',
    onContainer: '#ECE1FF',
    onColor: '#3A0092',
    onColorFixed: '#22005D',
    colorFixed: '#E9DDFF',
    onColorFixedDim: '#CFBCFF',
    onColorFixedVar: '#5400CC',
  },

  /** Secondary color used for accents and less prominent actions. */
  secondary: {
    main: '#4CD9F6',
    container: '#00AFCA',
    onColorFixed: '#001F26',
    colorFixed: '#A8EDFF',
  },

  /** terinary color used for miscellaneous and minor prominent actions. */
  tertiary: {
    container: '#F7A600',
    onColorFixed: '#291800',
    colorFixed: '#FFDDB3',
  },

  /** neautral colors */
  neutral: {
    neutral80: '#C9C5C8',
  },

  /** extended colors */
  extended: {
    green: {
      seed: '#76CA00',
    },
  },

  /** Color used to indicate errors in the application. */
  error: {
    main: '#FFB2BE',
    onColor: '#660025',
    error60: '#FF4E7C',
    error70: '#FF869D',
    error80: '#FFB2BE',
    error90: '#FFD9DE',
  },

  /** Color used to indicate warnings in the application. */
  warning: {
    main: '#ffca28',
  },

  /** Color used to convey informational messages. */
  info: {
    main: '#64c1ff',
  },

  /** Color used to indicate successful actions or states. */
  success: {
    main: '#CFBCFF',
  },

  /** Background colors for the UI. */
  background: {
    default: '#141219',
    paper: '#18161F',
    onBackground: '#E7E0EF',
  },

  /** Colors for banners displayed in the application. */
  banner: {
    info: '#64c1ff',
    error: '#FFB2BE',
    text: '#E5E1E5',
    link: '#768fff',
  },

  /** Background color for error messages or elements. */
  errorBackground: '#8c4351',

  /** Background color for warning messages or elements. */
  warningBackground: '#8f5e15',

  /** Background color for informational messages or elements. */
  infoBackground: '#343b58',

  /** Navigation-related colors. */
  navigation: {
    background: '#141316',
    indicator: '#8f5e15',
    color: '#d5d6db',
    selectedColor: '#ffffff',
  },

  /** Surface-related colors, defining the base layer and variations. */
  surface: {
    surface: '#141316',
    dim: '#141316',
    bright: '#3A393C',
    onSurface: '#E5E1E5',
    onSurfaceVar: '#C9C5CF',
    outline: '#928F99',
    outlineVariant: '#48464E',
  },

  /** Inverse-related colors */
  inverse: {
    surface: '#E5E1E5',
    onSurface: '#313033',
    primary: '#6E25F6',
  },

  /** Container surface colors for different elevation levels. */
  surfaceContainer: {
    container: '#24212E',
    high: '#2C2838',
    highest: '#383347',
    low: '#18161F',
    lowest: '#141219',
  },

  /** Hover effect color for surface elements. */
  surfaceHover: {
    onSurfaceVar: '#222225',
  },

  /** Status colors representing various system states. */
  status: {
    ok: '#1ee938',
    error: '#E91E63',
    running: '#D0CDD1',
    pending: '#ff8f00',
    aborted: '#8c4351',
    warning: '#ffca28',
  },
} as PaletteOptions;
